import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { XMarkIcon } from '@heroicons/react/24/outline'
import api from '../lib/api'
import type { FilterGroup, SuppressionList, PaginatedResponse } from '../types'
import toast from 'react-hot-toast'

interface AddToSuppressionModalProps {
  datasetId: string
  filter: FilterGroup | null
  rowCount: number
  onClose: () => void
}

export default function AddToSuppressionModal({ 
  datasetId, 
  filter, 
  rowCount, 
  onClose 
}: AddToSuppressionModalProps) {
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<'append' | 'create'>('append')
  const [selectedListId, setSelectedListId] = useState<string>('')
  const [newListName, setNewListName] = useState<string>('')
  const [newListDescription, setNewListDescription] = useState<string>('')
  
  // Fetch available suppression lists
  const { data: suppressionLists, isLoading } = useQuery({
    queryKey: ['suppression-lists'],
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<SuppressionList>>('/suppression-lists')
      return res.data.items
    },
  })
  
  const addMutation = useMutation({
    mutationFn: async () => {
      if (mode === 'append') {
        if (!selectedListId) throw new Error('No suppression list selected')
        
        const res = await api.post(
          `/suppression-lists/${selectedListId}/import-from-dataset`,
          {
            dataset_id: datasetId,
            filter_def: filter || undefined,
          }
        )
        
        return { ...res.data, mode: 'append' }
      } else {
        if (!newListName.trim()) throw new Error('List name is required')
        
        const res = await api.post(
          '/suppression-lists/create-from-dataset',
          {
            dataset_id: datasetId,
            name: newListName,
            description: newListDescription || undefined,
            filter_def: filter || undefined,
          }
        )
        
        return { ...res.data, mode: 'create' }
      }
    },
    onSuccess: (data: any) => {
      if (data.mode === 'append') {
        toast.success(data.message || `Added ${data.rows_added} rows to suppression list`)
      } else {
        toast.success(`Created new suppression list "${data.name}" with ${data.row_count} rows`)
      }
      queryClient.invalidateQueries({ queryKey: ['suppression-lists'] })
      queryClient.invalidateQueries({ queryKey: ['suppression-rows'] })
      onClose()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to add rows to suppression list')
    },
  })
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (mode === 'append' && !selectedListId) {
      toast.error('Please select a suppression list')
      return
    }
    if (mode === 'create' && !newListName.trim()) {
      toast.error('Please enter a name for the new suppression list')
      return
    }
    addMutation.mutate()
  }
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Add to Suppression List</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <p className="text-sm text-gray-600 mb-3">
              {filter 
                ? `Add ${rowCount.toLocaleString()} filtered rows to a suppression list`
                : `Add all rows to a suppression list`
              }
            </p>
            
            {/* Mode Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Action *
              </label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="append"
                    checked={mode === 'append'}
                    onChange={(e) => setMode(e.target.value as 'append')}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Append to existing list</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="create"
                    checked={mode === 'create'}
                    onChange={(e) => setMode(e.target.value as 'create')}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Create new list</span>
                </label>
              </div>
            </div>
            
            {/* Append Mode - Select existing list */}
            {mode === 'append' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Suppression List *
                </label>
                
                {isLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
                  </div>
                ) : suppressionLists && suppressionLists.length > 0 ? (
                  <select
                    value={selectedListId}
                    onChange={(e) => setSelectedListId(e.target.value)}
                    className="input"
                    required
                  >
                    <option value="">Choose a suppression list...</option>
                    {suppressionLists.map((list) => (
                      <option key={list.id} value={list.id}>
                        {list.name} ({list.row_count.toLocaleString()} rows)
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="text-sm text-gray-500 py-3 px-4 bg-gray-50 rounded-md">
                    No suppression lists available. Create a new one instead.
                  </div>
                )}
              </div>
            )}
            
            {/* Create Mode - New list details */}
            {mode === 'create' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    List Name *
                  </label>
                  <input
                    type="text"
                    value={newListName}
                    onChange={(e) => setNewListName(e.target.value)}
                    className="input"
                    placeholder="e.g., Unsubscribed Users"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description (optional)
                  </label>
                  <textarea
                    value={newListDescription}
                    onChange={(e) => setNewListDescription(e.target.value)}
                    className="input"
                    rows={2}
                    placeholder="Describe the purpose of this suppression list..."
                  />
                </div>
              </div>
            )}
          </div>
          
          {filter && (
            <div className="bg-gray-50 p-3 rounded-md">
              <div className="text-xs font-medium text-gray-700 mb-1">Current Filter:</div>
              <div className="text-xs text-gray-600 font-mono max-h-32 overflow-y-auto">
                {JSON.stringify(filter, null, 2)}
              </div>
            </div>
          )}
          
          {mode === 'append' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
              <p className="text-xs text-yellow-800">
                <strong>Note:</strong> Only matching column names will be copied. Missing columns will be set to null.
              </p>
            </div>
          )}
          
          {mode === 'create' && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-xs text-blue-800">
                <strong>Note:</strong> A new suppression list will be created with all columns from the dataset.
              </p>
            </div>
          )}
          
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={addMutation.isPending || (mode === 'append' && !selectedListId) || (mode === 'create' && !newListName.trim())}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {addMutation.isPending 
                ? (mode === 'create' ? 'Creating...' : 'Adding...') 
                : (mode === 'create' ? 'Create Suppression List' : 'Add to Suppression List')
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
